'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { ArrowLeft, ArrowRight, ArrowUpRight } from '@phosphor-icons/react';
import { CheckIcon, ShareIcon } from '@/components/icons';
import { Header, Footer } from '@/components/layout';
import { blogApi, type BlogPostFull } from '@/lib/api/blog';
import { siteConfig } from '@/config/site';
import {
  BlogCard,
  BlogCover,
  BlogMeta,
  BlogGridSkeleton,
} from '@/components/features/blog/blog-editorial';
import '@/components/features/blog/blog-editorial.css';

interface Props {
  slug: string;
  initialPost: BlogPostFull | null;
}

export function BlogPostClient({ slug, initialPost }: Props) {
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const {
    data: post,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => blogApi.getBySlug(slug),
    staleTime: 60000,
    enabled: !!slug,
    initialData: initialPost ?? undefined,
  });

  async function handleShare() {
    const url = `${siteConfig.url}/blog/${slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: post?.title, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setShareStatus('copied');
      } else {
        setShareStatus('error');
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) setShareStatus('error');
    }
  }

  return (
    <div className="blog-editorial journal-page">
      <Header />
      <main className="journal-container journal-main journal-article-main">
        <nav className="journal-article-nav" aria-label="Навигация по блогу">
          <Link href="/blog" className="journal-back">
            <ArrowLeft size={18} aria-hidden="true" />
            Все статьи
          </Link>
          <span>Блог Азиаточки</span>
        </nav>
        {isLoading ? (
          <div className="journal-article-loading">
            <BlogGridSkeleton count={1} />
            <div className="journal-skeleton">
              <span />
              <span />
              <span />
            </div>
          </div>
        ) : !post ? (
          <div className="journal-empty" role={isError ? 'alert' : undefined}>
            <h1>{isError ? 'Не получилось открыть статью' : 'Статья не найдена'}</h1>
            <p>
              {isError
                ? 'Попробуйте обновить страницу или вернитесь к другим материалам.'
                : 'Возможно, её адрес изменился. Другие материалы доступны в блоге.'}
            </p>
            <div className="journal-empty-actions">
              {isError && (
                <button type="button" className="journal-button" onClick={() => void refetch()}>
                  Повторить
                </button>
              )}
              <Link href="/blog" className="journal-text-link">
                К блогу <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </div>
        ) : (
          <>
            <article>
              <header
                className={`journal-article-hero${post.image_url ? '' : ' journal-article-hero-text'}`}
              >
                <div className="journal-article-heading">
                  <div className="journal-article-tags">
                    {post.tags
                      .filter((tag) => tag !== 'Архив')
                      .map((tag) => (
                        <Link key={tag} href={`/blog?tag=${encodeURIComponent(tag)}`}>
                          {tag}
                        </Link>
                      ))}
                    {post.is_archive && <span className="journal-archive-label">Архив</span>}
                  </div>
                  <h1>{post.title}</h1>
                  {post.excerpt && <p className="journal-article-deck">{post.excerpt}</p>}
                  <BlogMeta post={post} />
                </div>
                {post.image_url && (
                  <div className="journal-article-art">
                    <BlogCover post={post} featured priority />
                  </div>
                )}
              </header>
              <div className="journal-reading">
                {post.is_archive && (
                  <aside className="journal-archive-note">
                    <span>Архивный материал</span>
                    <p>
                      Условия, цены и ассортимент могли измениться. Актуальные предложения —{' '}
                      <Link href="/actions">в разделе акций</Link>, условия —{' '}
                      <Link href="/delivery">на странице доставки</Link>.
                    </p>
                  </aside>
                )}
                <div className="journal-prose" dangerouslySetInnerHTML={{ __html: post.content }} />
                <div className="journal-article-end">
                  <span>Азиаточка</span>
                  <div>
                    <button
                      type="button"
                      className="journal-share"
                      onClick={() => void handleShare()}
                    >
                      {shareStatus === 'copied' ? (
                        <CheckIcon size={18} aria-hidden="true" />
                      ) : (
                        <ShareIcon size={18} aria-hidden="true" />
                      )}
                      {shareStatus === 'copied' ? 'Ссылка скопирована' : 'Поделиться'}
                    </button>
                    <span className="journal-share-status" role="status">
                      {shareStatus === 'error'
                        ? 'Не удалось поделиться. Можно скопировать адрес из строки браузера.'
                        : ''}
                    </span>
                  </div>
                </div>
                <div className="journal-reading-links">
                  <Link className="journal-text-link" href="/blog">
                    <ArrowLeft size={18} aria-hidden="true" />
                    Все статьи
                  </Link>
                  <Link className="journal-button" href="/">
                    Перейти к меню <ArrowUpRight size={18} aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </article>
            {!!post.related?.length && (
              <section className="journal-related" aria-labelledby="journal-related-heading">
                <header className="journal-home-intro">
                  <h2 id="journal-related-heading">Ещё почитать</h2>
                  <Link href="/blog" className="journal-text-link">
                    Весь блог <ArrowUpRight size={18} aria-hidden="true" />
                  </Link>
                </header>
                <div className="journal-grid">
                  {post.related.slice(0, 3).map((related) => (
                    <BlogCard post={related} heading="h3" key={related.id} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
